import { useCallback, useMemo, useRef } from 'react';

import type { GroupBase, MenuListProps } from 'react-select';

import { useVirtualizer } from '@tanstack/react-virtual';
import isEqual from 'fast-deep-equal';

import { flattenOptions } from '#select/utilities/flatten-options.util';

import { useVirtualSelectConfiguration } from './virtual-select-configuration.hook';

/**
 * Custom hook that provides virtualization logic for rendering large lists in a select component.
 * Utilizes `@tanstack/react-virtual` for efficient rendering and scrolling.
 *
 * @template Option - The type of the option objects.
 * @template IsMulti - Boolean indicating if multiple selections are allowed.
 * @template Group - The type of the group objects, extending GroupBase<Option>.
 *
 * @param params - An object containing:
 *   @param params.children - The children elements to be rendered in the menu list.
 *   @param params.options - The options to be displayed in the select component.
 *
 * @returns An object containing:
 *   - `currentIndex`: A function to get the index of a given option in the flattened options array.
 *   - `flattenedChildren`: The flattened list of children elements for rendering.
 *   - `items`: The virtualized items to be rendered.
 *   - `measureRef`: Ref callback for measuring the size of elements.
 *   - `parentRef`: Ref to the parent scrollable container.
 *   - `scrollTo`: Function to scroll to a specific index.
 *   - `totalSize`: The total size (height) of all virtualized items.
 */
export const useVirtual = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  children,
  options,
}: Pick<MenuListProps<Option, IsMulti, Group>, 'children' | 'options'>) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const { estimatedSize, flattenedChildren } = useVirtualSelectConfiguration({ children });
  const count = useMemo(() => {
    return flattenedChildren.length;
  }, [flattenedChildren.length]);

  const flattenedOptions: (Group | Option)[] = useMemo(() => {
    return flattenOptions(options);
  }, [options]);

  const currentIndex = useCallback(
    (option: Option) => {
      return flattenedOptions.findIndex((flattened) => {
        return isEqual(flattened, option);
      });
    },
    [flattenedOptions]
  );

  const virtualizer = useVirtualizer({
    count,
    estimateSize: () => {
      return estimatedSize;
    },
    getScrollElement: () => {
      return parentRef.current;
    },
    overscan: 5,
  });

  return {
    currentIndex,
    flattenedChildren,
    items: virtualizer.getVirtualItems(),
    measureRef: virtualizer.measureElement,
    parentRef,
    scrollTo: virtualizer.scrollToIndex,
    totalSize: virtualizer.getTotalSize(),
  };
};
